import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateCard } from 'apps/shared/scripts/graphql';
import { useFormCallback } from 'apps/shared/scripts/hooks';

import { OnboardingFormValues } from '../../components/OnboardingForm';

import OnboardingFormRouter from './OnboardingFormRouter';

interface Props {
  isAuthenticated: boolean;
  onUnauthenticated: (formData: OnboardingFormValues) => void;
  stashedFormData?: OnboardingFormValues;
}

const OnboardingFormContainer = ({
  isAuthenticated,
  onUnauthenticated,
  stashedFormData,
}: Props) => {
  const { mutate: createCard, isMutating: isCreating } = useCreateCard({
    refetchQueries: ['Cards'],
    awaitRefetchQueries: true,
  });
  const { onSuccess, onError } = useFormCallback();
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (data: OnboardingFormValues) => {
      if (isAuthenticated) {
        try {
          const { result, errors } = await createCard({ input: data });
          if (!result || errors.length > 0) {
            throw new Error(errors[0].message);
          }
          onSuccess({ message: 'Success! Card was created' }, () => {
            navigate(`/${result.id}`);
          });
        } catch (e) {
          onError(e);
          throw e;
        }
      } else {
        onUnauthenticated(data);
      }
    },
    [
      createCard,
      isAuthenticated,
      navigate,
      onError,
      onSuccess,
      onUnauthenticated,
    ],
  );

  return (
    <OnboardingFormRouter
      defaultValues={stashedFormData}
      onSubmit={onSubmit}
      isSubmitting={isCreating}
    />
  );
};

OnboardingFormContainer.displayName = 'OnboardingFormContainer';

export default OnboardingFormContainer;
